import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Typography,
  ButtonGroup,
  ButtonGroupTypes,
  FormInput,
  Dropdown,
  Alert,
} from '@ecdlink/ui';
import { Controller, useForm, useFormState } from 'react-hook-form';
import { useEffect, useMemo, useState, ChangeEvent } from 'react';
import { MotherDetailsProps, yesNoOptions } from './mother-details.types';
import {
  MotherDetailsModel,
  motherDetailsModelSchema,
} from '@/schemas/infant/mother-details';
import { useSelector } from 'react-redux';
import { caregiverSelectors } from '@/store/caregiver';
import { motherSelectors } from '@/store/mother';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { InfantActions } from '@/store/infant/infant.actions';
import { EventRecordActions } from '@/store/eventRecord/eventRecord.actions';
import { staticDataSelectors } from '@/store/static-data';

export const MOTHER_TYPE_ID = '568a219f-f1b9-41ac-bf38-143d8d749a39';

export const MotherDetails: React.FC<MotherDetailsProps> = ({
  onSubmit,
  setAddress,
  setContactInformation,
  setIsAlreadyClient,
  isAlreadyClient,
  infantDetails,
  multipleChildrenArray,
  setMultipleChildrenArray,
  motherInfo,
  existingDetails,
}) => {
  const {
    watch,
    getValues: getMotherDetailsFormValues,
    setValue: setMotherDetailsFormValue,
    register: caregiverFormRegister,
    control: motherDetailsFormControl,
    trigger,
  } = useForm<MotherDetailsModel>({
    resolver: yupResolver(motherDetailsModelSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      id: existingDetails?.id || '',
      name: existingDetails?.name || '',
      surname: existingDetails?.surname || '',
      age: existingDetails?.age || '',
      relationshipId: existingDetails?.relationshipId || '',
      isMother: existingDetails?.isMother || false,
    },
  });
  const { isValid } = useFormState({ control: motherDetailsFormControl });
  const caregivers =
    useSelector(caregiverSelectors.getCaregiversAndNotMothers) || [];
  const mothers = useSelector(motherSelectors?.getMothers);
  const relations = useSelector(staticDataSelectors.getRelations);
  const [buttonGroupOptions, setButtonGroupOptions] = useState(yesNoOptions);

  const { isLoading } = useThunkFetchCall('infants', InfantActions.ADD_INFANTS);
  const { isLoading: isLoadingUpdateInfantCaregiver } = useThunkFetchCall(
    'infants',
    InfantActions.UPDATE_INFANT_CAREGIVER
  );
  const { isLoading: isLoadingEventRecord } = useThunkFetchCall(
    'eventRecord',
    EventRecordActions.ADD_EVENT_RECORD
  );

  const [ageAnswer, setAgeAnswer] = useState(existingDetails?.age || '');
  const ageRegex =
    /^(1[1-9](?:\.\d{1,3})?|[2-9]\d(?:\.\d{1,3})?|100(?:\.0{1,3})?)$/;

  const motherType = relations.find((item) => item.description === 'Mother');
  const caregiverType = relations.find(
    (item) => item.id === motherInfo?.relationId
  );

  const mothersUpdatedToCaregivers = mothers?.map((item) => ({
    firstName: item?.user?.firstName,
    surname: item?.user?.surname,
    phoneNumber: item?.user?.phoneNumber,
    siteAddress: item?.siteAddress,
    isActive: item?.isActive,
    id: item?.user?.id,
    isMother: true,
    age: '',
  }));

  const motherAndCaregivers = [...caregivers, ...mothersUpdatedToCaregivers];

  const isCaregiver = useMemo(
    () => !!motherInfo?.user || !!motherInfo?.id,
    [motherInfo]
  );

  const isShowCaregiversDropdown = useMemo(
    () =>
      isCaregiver
        ? isCaregiver && isAlreadyClient
        : isAlreadyClient && !!motherAndCaregivers?.length,
    [isAlreadyClient, motherAndCaregivers?.length, isCaregiver]
  );

  // Initialize form values from existingDetails when available
  useEffect(() => {
    if (existingDetails) {
      if (existingDetails.id) {
        setMotherDetailsFormValue('id', existingDetails.id);
      }
      if (existingDetails.name) {
        setMotherDetailsFormValue('name', existingDetails.name);
      }
      if (existingDetails.surname) {
        setMotherDetailsFormValue('surname', existingDetails.surname);
      }
      if (existingDetails.age) {
        setMotherDetailsFormValue('age', existingDetails.age);
        setAgeAnswer(existingDetails.age);
      }
      if (existingDetails.relationshipId) {
        setMotherDetailsFormValue(
          'relationshipId',
          existingDetails.relationshipId
        );
      }
      if (existingDetails.isMother !== undefined) {
        setMotherDetailsFormValue('isMother', existingDetails.isMother);
      }

      // Trigger validation after setting all values
      trigger();
    }
  }, [existingDetails, setMotherDetailsFormValue, trigger]);

  useEffect(() => {
    watch();
  }, [watch]);

  useEffect(() => {
    if (isCaregiver) {
      setButtonGroupOptions((prevState) =>
        prevState.map((item) => ({
          ...item,
          disabled: true,
        }))
      );
      setIsAlreadyClient(true);
      setMotherDetailsFormValue('id', motherInfo?.user?.id || motherInfo?.id);
      setMotherDetailsFormValue(
        'name',
        motherInfo?.user?.firstName || motherInfo?.firstName
      );
      setMotherDetailsFormValue(
        'surname',
        motherInfo?.user?.surname || motherInfo?.surname
      );
      setMotherDetailsFormValue('isMother', !!motherInfo?.user?.id);

      const resolvedRelationId = caregiverType?.id || motherType?.id;
      if (resolvedRelationId) {
        setMotherDetailsFormValue('relationshipId', resolvedRelationId);
      }

      trigger();
    }
  }, [
    caregiverType?.id,
    isCaregiver,
    motherInfo,
    motherType,
    setIsAlreadyClient,
    setMotherDetailsFormValue,
    trigger,
  ]);

  const handleAgeChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setAgeAnswer(value);
  };

  const validAge = useMemo(
    () => ageAnswer && ageRegex.test(String(ageAnswer).replaceAll(',', '.')),
    [ageAnswer]
  );

  return (
    <>
      <div>
        <Typography
          type="h2"
          color={'textDark'}
          text={'Caregiver'}
          className="pt-6"
        />
        <Typography
          type="h4"
          color={'textMid'}
          text={'Details'}
          className="w-11/12 pt-2"
        />
      </div>
      <div className="flex w-11/12 justify-center text-red-400">
        <Divider dividerType="dashed" />
      </div>
      <div className="mb-2 w-full">
        {!!multipleChildrenArray?.length ? (
          multipleChildrenArray?.map((child, index) => {
            return (
              <div key={index}>
                <Typography
                  key={index + 2}
                  type="h3"
                  color={'textDark'}
                  text={`Relationship to ${child?.firstName}`}
                  className="bt-1 w-11/12 pt-2"
                />
                <Dropdown
                  key={index + 3}
                  placeholder={'Please choose the client:'}
                  fillType="clear"
                  disabled={isCaregiver}
                  selectedValue={
                    isCaregiver
                      ? caregiverType?.id || motherType?.id
                      : getMotherDetailsFormValues('relationshipId') ||
                        multipleChildrenArray[index].relationshipId
                  }
                  list={
                    (relations &&
                      relations
                        .filter((x) => x.description?.length > 0)
                        .map((item) => {
                          return {
                            label: item.description,
                            value: item.id,
                          };
                        })) ||
                    []
                  }
                  onChange={(value) => {
                    setMultipleChildrenArray(
                      multipleChildrenArray.map((item, currentIndex) =>
                        currentIndex === index
                          ? { ...item, relationshipId: value }
                          : item
                      )
                    );
                  }}
                />
              </div>
            );
          })
        ) : (
          <>
            <Typography
              type="h3"
              color={'textDark'}
              text={`Relationship to ${infantDetails?.firstName}`}
              className="bt-1 w-11/12 pt-2"
            />
            <Controller
              name="relationshipId"
              control={motherDetailsFormControl}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  disabled={isCaregiver}
                  placeholder={'Select relationship:'}
                  fillType="clear"
                  selectedValue={
                    isCaregiver ? caregiverType?.id || motherType?.id : value
                  }
                  list={
                    (relations &&
                      relations
                        .filter((x) => x.description?.length > 0)
                        .map((item) => {
                          return {
                            label: item.description,
                            value: item.id,
                          };
                        })) ||
                    []
                  }
                  onChange={onChange}
                />
              )}
            />
          </>
        )}
      </div>
      <div>
        <Typography
          type="h3"
          color={'textDark'}
          text={'Is the caregiver already on CHW Connect?'}
          className="w-11/12 pt-2"
        />
        <div className="mt-2">
          <ButtonGroup<boolean>
            selectedOptions={isAlreadyClient}
            options={buttonGroupOptions}
            onOptionSelected={(value: boolean | boolean[]) => {
              setIsAlreadyClient(value);
              setMotherDetailsFormValue('id', '');
              setMotherDetailsFormValue('name', '');
              setMotherDetailsFormValue('surname', '');
              if (value === false) {
                multipleChildrenArray?.map((child, index) => {
                  return (multipleChildrenArray[index].caregiver = undefined);
                });
              }
            }}
            color="secondary"
            type={ButtonGroupTypes.Button}
            className={'w-full'}
          />
        </div>

        {isAlreadyClient === false && (
          <>
            <FormInput<MotherDetailsModel>
              label={'First name'}
              register={caregiverFormRegister}
              nameProp={'name'}
              placeholder={'Enter a name'}
              type={'text'}
              className="mt-4"
              value={existingDetails?.name}
            ></FormInput>
            <FormInput<MotherDetailsModel>
              label={'Surname'}
              register={caregiverFormRegister}
              nameProp={'surname'}
              placeholder={'Enter a surname'}
              type={'text'}
              className="mt-4"
              value={existingDetails?.surname}
            ></FormInput>
            <div className="flex items-center gap-1">
              <FormInput<MotherDetailsModel>
                label={'Age'}
                register={caregiverFormRegister}
                nameProp={'age'}
                placeholder={'Enter an age'}
                type={'number'}
                className="mt-4 w-1/2"
                value={existingDetails?.age}
                onChange={handleAgeChange}
              ></FormInput>
              <Typography
                type="h4"
                color={'textMid'}
                text={'years'}
                className="mt-12"
              />
            </div>
            <div className="mt-3">
              {ageAnswer !== '' &&
                !ageRegex.test(String(ageAnswer).replaceAll(',', '.')) && (
                  <Alert
                    className="mb-4"
                    type="error"
                    title="Please enter an age between 11 and 100."
                  />
                )}
            </div>
          </>
        )}
        {isShowCaregiversDropdown && (
          <div className="mt-4 w-full">
            <Typography
              type="h3"
              color={'textDark'}
              text={`Choose caregiver`}
              className="bt-1 w-11/12 pt-2"
            />
            <Controller
              name="id"
              control={motherDetailsFormControl}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  placeholder={'Please choose the client:'}
                  fillType="clear"
                  disabled={isCaregiver}
                  selectedValue={
                    !!motherInfo?.user?.id ? motherInfo.user.id : value
                  }
                  list={
                    (motherAndCaregivers &&
                      motherAndCaregivers
                        .filter((x) => x.id && x.firstName?.length! > 0)
                        .map((item) => {
                          return {
                            label: item.firstName! + ' ' + item.surname!,
                            value: item.id,
                          };
                        })) ||
                    []
                  }
                  onChange={(value) => {
                    const caregiver = motherAndCaregivers?.find(
                      (item) => item.id === value
                    );

                    if (multipleChildrenArray?.length) {
                      setMultipleChildrenArray(
                        multipleChildrenArray.map((child) => ({
                          ...child,
                          caregiver,
                        }))
                      );
                    }

                    setMotherDetailsFormValue('name', caregiver?.firstName);
                    setMotherDetailsFormValue('surname', caregiver?.surname);
                    setMotherDetailsFormValue('age', caregiver?.age);
                    if (caregiver?.isMother === true) {
                      setMotherDetailsFormValue('isMother', true);
                    }
                    onChange(value);
                  }}
                />
              )}
            />
          </div>
        )}
      </div>
      {isAlreadyClient !== false && !motherAndCaregivers?.length && (
        <Alert
          className={'mt-5 mb-3'}
          message={
            "You don't have any clients yet! Choose &quot;No&quot; above to continue."
          }
          type={'info'}
        />
      )}
      <div className="flex h-full items-end">
        <Button
          type={'filled'}
          color={'primary'}
          className="mt-4 w-full"
          textColor={'white'}
          text={isAlreadyClient ? 'Save' : 'Next'}
          icon={isAlreadyClient ? 'SaveIcon' : 'ArrowCircleRightIcon'}
          iconPosition={'start'}
          onClick={() => {
            onSubmit(getMotherDetailsFormValues());
          }}
          isLoading={
            isLoading || isLoadingUpdateInfantCaregiver || isLoadingEventRecord
          }
          disabled={
            isLoading ||
            isLoadingUpdateInfantCaregiver ||
            isLoadingEventRecord ||
            (!multipleChildrenArray?.length &&
              !getMotherDetailsFormValues('relationshipId')) ||
            isAlreadyClient === null ||
            (!multipleChildrenArray?.length &&
              !isValid &&
              isAlreadyClient === false) ||
            (isAlreadyClient === true && !getMotherDetailsFormValues('id')) ||
            (isAlreadyClient === false && !validAge) ||
            (!isCaregiver &&
              multipleChildrenArray &&
              multipleChildrenArray?.length > 0 &&
              multipleChildrenArray?.filter((child) => child?.relationshipId)
                .length !== multipleChildrenArray?.length)
          }
        />
      </div>
    </>
  );
};
